import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Trophy, Target, Clock, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface UpcomingMatch {
  id: string;
  match_date: string;
  team_a: string;
  team_b: string;
  player_a: { id: string; name: string; avatar: string | null };
  player_b: { id: string; name: string; avatar: string | null };
}

interface Prediction {
  match_id: string;
  predicted_winner_id: string | null;
  is_draw_prediction: boolean;
}

export const MatchPrediction = () => {
  const { user } = useAuth();
  const [matches, setMatches] = useState<UpcomingMatch[]>([]);
  const [predictions, setPredictions] = useState<Map<string, Prediction>>(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<string | null>(null);

  useEffect(() => {
    fetchUpcomingMatches();
    if (user) fetchUserPredictions();
  }, [user]);

  const fetchUpcomingMatches = async () => {
    const { data } = await supabase
      .from('matches')
      .select(`
        id, match_date, team_a, team_b,
        player_a:players!matches_team_a_fkey(id, name, avatar),
        player_b:players!matches_team_b_fkey(id, name, avatar)
      `)
      .eq('status', 'scheduled')
      .not('team_a', 'is', null)
      .not('team_b', 'is', null)
      .order('match_date', { ascending: true })
      .limit(5);

    if (data) {
      setMatches(data.map(m => ({
        ...m,
        player_a: m.player_a as any,
        player_b: m.player_b as any
      })));
    }
    setLoading(false);
  };

  const fetchUserPredictions = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('match_predictions')
      .select('match_id, predicted_winner_id, is_draw_prediction')
      .eq('user_id', user.id);

    if (data) {
      const predMap = new Map();
      data.forEach(p => predMap.set(p.match_id, p));
      setPredictions(predMap);
    }
  };

  const submitPrediction = async (matchId: string, winnerId: string | null, isDraw: boolean) => {
    if (!user) {
      toast.error('প্রেডিকশন করতে লগইন করুন');
      return;
    }

    setSubmitting(matchId);
    try {
      const { error } = await supabase
        .from('match_predictions')
        .upsert({
          match_id: matchId,
          user_id: user.id,
          predicted_winner_id: isDraw ? null : winnerId,
          is_draw_prediction: isDraw
        }, { onConflict: 'match_id,user_id' });

      if (error) throw error;

      setPredictions(prev => {
        const newMap = new Map(prev);
        newMap.set(matchId, {
          match_id: matchId,
          predicted_winner_id: isDraw ? null : winnerId,
          is_draw_prediction: isDraw
        });
        return newMap;
      });

      toast.success('প্রেডিকশন সেভ হয়েছে!');
    } catch (error) {
      toast.error('প্রেডিকশন সেভ করতে সমস্যা হয়েছে');
    } finally {
      setSubmitting(null);
    }
  };

  const getPrediction = (matchId: string) => predictions.get(matchId);

  if (loading) {
    return (
      <Card className="p-4 animate-pulse">
        <div className="h-40 bg-muted rounded" />
      </Card>
    );
  }

  if (matches.length === 0) {
    return (
      <Card className="p-4 bg-gradient-to-br from-card/80 to-card border-primary/20">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="font-bold text-lg">Match Prediction</h3>
        </div>
        <div className="text-center text-muted-foreground py-8">
          কোন আসন্ন ম্যাচ নেই
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-gradient-to-br from-card/80 to-card border-primary/20">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-primary" />
        <h3 className="font-bold text-lg">Match Prediction</h3>
        <Badge variant="secondary" className="ml-auto">
          <Trophy className="w-3 h-3 mr-1" />
          পয়েন্ট জিতুন
        </Badge>
      </div>

      <div className="space-y-3">
        {matches.slice(0, 3).map(match => {
          const prediction = getPrediction(match.id);
          const hasPredicted = !!prediction;

          return (
            <div key={match.id} className="bg-background/50 rounded-lg p-3 border border-border/50">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {format(new Date(match.match_date), 'dd MMM, hh:mm a')}
                </div>
                {hasPredicted && (
                  <Badge variant="outline" className="text-green-500 border-green-500/30">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Predicted
                  </Badge>
                )}
              </div>

              <div className="flex items-center justify-between gap-2">
                {/* Player A */}
                <button
                  onClick={() => submitPrediction(match.id, match.player_a.id, false)}
                  disabled={submitting === match.id}
                  className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${
                    prediction?.predicted_winner_id === match.player_a.id
                      ? 'bg-primary/20 border-2 border-primary'
                      : 'bg-muted/50 hover:bg-muted border-2 border-transparent'
                  }`}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={match.player_a.avatar || ''} />
                    <AvatarFallback>{match.player_a.name[0]}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs font-medium truncate w-full text-center">
                    {match.player_a.name.split(' ')[0]}
                  </span>
                </button>

                {/* Draw */}
                <button
                  onClick={() => submitPrediction(match.id, null, true)}
                  disabled={submitting === match.id}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    prediction?.is_draw_prediction
                      ? 'bg-yellow-500/20 border-2 border-yellow-500 text-yellow-500'
                      : 'bg-muted/50 hover:bg-muted border-2 border-transparent'
                  }`}
                >
                  ড্র
                </button>

                {/* Player B */}
                <button
                  onClick={() => submitPrediction(match.id, match.player_b.id, false)}
                  disabled={submitting === match.id}
                  className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${
                    prediction?.predicted_winner_id === match.player_b.id
                      ? 'bg-primary/20 border-2 border-primary'
                      : 'bg-muted/50 hover:bg-muted border-2 border-transparent'
                  }`}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={match.player_b.avatar || ''} />
                    <AvatarFallback>{match.player_b.name[0]}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs font-medium truncate w-full text-center">
                    {match.player_b.name.split(' ')[0]}
                  </span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground text-center mt-3">
        সঠিক প্রেডিকশনে পয়েন্ট পাবেন!
      </p>
    </Card>
  );
};
